import {Controller} from "stimulus";
import {getNearestOverflowAncestor} from "@floating-ui/utils/dom";
import scrollIntoView from "scroll-into-view-if-needed";

export default class extends Controller<HTMLDetailsElement> {
  static values = {position: String};
  static targets = ["popover"];

  declare positionValue: "below" | "above" | "left" | "right";
  declare popoverTarget: HTMLElement;

  connect() {
    document.addEventListener("click", this.clickOutside);
    document.addEventListener("keyup", this.escape);
    this.element.addEventListener("toggle", this.handleToggle);
  }

  disconnect() {
    document.removeEventListener("click", this.clickOutside);
    document.removeEventListener("keyup", this.escape);
    this.element.removeEventListener("toggle", this.handleToggle);
  }

  close = () => {
    this.element.removeAttribute("open");
  };

  clickOutside = ({target}) => {
    if (this.element.open && !this.element.contains(target)) {
      this.close();
    }
  };

  escape = ({keyCode}) => {
    if (this.element.open && keyCode === 27) {
      this.close();
    }
  };

  handleToggle = () => {
    if (this.element.open) {
      // Flip menu orientation based on screen position
      this.handlePopoverOrientation();

      // Nudge popover back into viewport if overflowing
      this.adjustForViewport();

      // Ensure element is within viewport
      scrollIntoView(this.menuElement, {
        scrollMode: "if-needed",
        inline: "nearest",
        block: "nearest",
      });

      // Focus any elements expecting autofocus
      this.element.querySelector<HTMLElement>("[autofocus]")?.focus();
    } else {
      this.menuParentElement.style.transform = "";
    }
  };

  private handlePopoverOrientation = () => {
    if (this.overFlipThreshold || this.isOutOfBounds) {
      this.element.dataset.flipped = "true";
    } else {
      delete this.element.dataset.flipped;
    }

    // Ensure element is within scrollable area
    if (this.isOutOfBounds && this.element.dataset.flipped) {
      delete this.element.dataset.flipped;
    } else if (this.isOutOfBounds) {
      this.element.dataset.flipped = "true";
    }
  };

  private get overFlipThreshold() {
    const rect = this.element.getBoundingClientRect();

    switch (this.positionValue) {
    case "above":
      return rect.bottom / window.innerHeight < 0.5;
    case "below":
      return rect.top / window.innerHeight > 0.5;
    case "left":
      return rect.right / window.innerWidth < 0.5;
    case "right":
      return rect.left / window.innerWidth > 0.5;
    default:
      return false;
    }
  }

  private get scrollParent() {
    return getNearestOverflowAncestor(this.element);
  }

  private get menuElement() {
    return this.popoverTarget.querySelector("[role=menu]") as HTMLElement;
  }

  private get menuParentElement() {
    return this.menuElement.parentElement as HTMLElement;
  }

  private get isOutOfBounds() {
    const menuRect = this.menuElement.getBoundingClientRect();
    const parentRect = this.scrollParent.getBoundingClientRect();

    if (menuRect.top < parentRect.top || menuRect.bottom > parentRect.bottom) return true;

    return false;
  }

  private adjustForViewport = () => {
    const menu = this.menuParentElement;
    if (!menu) return;

    menu.style.transform = "";
    const rect = menu.getBoundingClientRect();
    let offsetX = 0;

    if (rect.left < 0) {
      offsetX = -rect.left;
    } else if (rect.right > window.innerWidth) {
      offsetX = window.innerWidth - rect.right;
    }

    if (offsetX !== 0) {
      menu.style.transform = `translateX(${offsetX}px)`;
    }
  };
}
